// Story 056 — resolve the active image's pixels into a histogram.
//
// Pipeline per image switch:
//   1. bytes via the CANONICAL image cache (`imageCache.fetchThrough`,
//      SB6-ANA-CACHE-01) — a hit costs zero network; a miss populates the
//      cache the Viewer/prefetch also read from, so the histogram never
//      double-fetches a full-res blob or fights SAS/CORS on its own.
//      While the prefetch queue is already downloading the SAME key (the
//      active image is its P0), we WAIT for its bytes to land instead of
//      starting a duplicate multi-MB download — the same cross-module
//      dedupe `useCachedImageUrl` applies via `prefetchScheduler.isHandling`;
//   2. decode + downsample to 256 × 256 in ONE step
//      (`createImageBitmap` resize — story Open Q5: the chart resolves
//      256 bins, a full-resolution decode of a 40 MP image gains nothing
//      visible and costs ~180 MB of transient bitmap);
//   3. bucket off-thread in the histogram Web Worker (last-write-wins —
//      a result for an image the analyst already left is discarded).
//
// jsdom note: component tests mock THIS module — it is the only place in
// the widget that touches fetch/canvas/worker.
import { useEffect, useRef, useState } from "react";
import type { ImageRef } from "@aeriosiq/types";
import {
  fetchThrough,
  get as cacheGet,
  ImageFetchError,
} from "../cache/imageCache.js";
import { canonicalKey } from "../cache/canonicalKey.js";
import { prefetchScheduler } from "../cache/prefetchQueue.js";
import type { HistogramResult } from "./computeHistogram.js";
import {
  createHistogramClient,
  StaleComputeError,
  type HistogramClient,
} from "./histogramWorkerClient.js";

/** Downsample edge length (px) fed to the worker — story Open Q5. */
export const HISTOGRAM_SAMPLE_SIZE = 256;

/** Poll interval while waiting on a prefetch-queue download of our key. */
const QUEUE_WAIT_POLL_MS = 150;

export type HistogramStatus = "idle" | "loading" | "ready" | "error";

export interface UseImageHistogramResult {
  /** `idle` = no image to sample (widget shows its empty state). */
  status: HistogramStatus;
  /** Non-null exactly when `status === "ready"`. */
  histogram: HistogramResult | null;
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Resolve the image bytes, deduped against the prefetch queue: while the
 * queue is handling this URL's canonical key (the active image is its P0,
 * so on a cache miss it usually is), poll the shared cache bucket until
 * the queue's download lands rather than fetching a duplicate copy —
 * the same coalescing `useCachedImageUrl` does before its populate fetch.
 * Falls through to `fetchThrough` (cache-probe + fetch) once the queue is
 * no longer handling the key (e.g. its fetch failed). Returns null when
 * the pipeline was cancelled mid-wait.
 */
async function resolveImageBlob(
  fullUrl: string,
  isCancelled: () => boolean,
): Promise<Blob | null> {
  const key = canonicalKey(fullUrl);
  while (prefetchScheduler.isHandling(fullUrl)) {
    const hit = await cacheGet(key);
    if (hit) return hit;
    if (isCancelled()) return null;
    await delay(QUEUE_WAIT_POLL_MS);
  }
  if (isCancelled()) return null;
  return fetchThrough(fullUrl);
}

/**
 * Decode `image` straight to a 256 × 256 RGBA buffer. The browser scales
 * DURING decode (`createImageBitmap` resize options) so the full-res
 * bitmap never materialises; `resizeQuality: "low"` is statistically
 * identical for bucketing purposes. Prefers OffscreenCanvas (no DOM node
 * churn); falls back to a detached <canvas> for engines without it.
 * Throws when no 2D context is obtainable.
 */
async function downsampleToImageData(blob: Blob): Promise<ImageData> {
  const bitmap = await createImageBitmap(blob, {
    resizeWidth: HISTOGRAM_SAMPLE_SIZE,
    resizeHeight: HISTOGRAM_SAMPLE_SIZE,
    resizeQuality: "low",
  });
  // Two explicit branches (rather than a canvas union) so each
  // `getContext` resolves to a CONCRETE context type — the typed lint
  // rules can't resolve calls through the Offscreen/HTML union.
  try {
    if (typeof OffscreenCanvas !== "undefined") {
      const canvas = new OffscreenCanvas(
        HISTOGRAM_SAMPLE_SIZE,
        HISTOGRAM_SAMPLE_SIZE,
      );
      const ctx = canvas.getContext("2d");
      if (!ctx) throw new Error("2D canvas context unavailable");
      ctx.drawImage(bitmap, 0, 0, HISTOGRAM_SAMPLE_SIZE, HISTOGRAM_SAMPLE_SIZE);
      return ctx.getImageData(
        0,
        0,
        HISTOGRAM_SAMPLE_SIZE,
        HISTOGRAM_SAMPLE_SIZE,
      );
    }
    const el = document.createElement("canvas");
    el.width = HISTOGRAM_SAMPLE_SIZE;
    el.height = HISTOGRAM_SAMPLE_SIZE;
    const ctx = el.getContext("2d");
    if (!ctx) throw new Error("2D canvas context unavailable");
    ctx.drawImage(bitmap, 0, 0, HISTOGRAM_SAMPLE_SIZE, HISTOGRAM_SAMPLE_SIZE);
    return ctx.getImageData(0, 0, HISTOGRAM_SAMPLE_SIZE, HISTOGRAM_SAMPLE_SIZE);
  } finally {
    bitmap.close();
  }
}

/**
 * Histogram of the given image, recomputed whenever the image (or its
 * signed URL, post SAS re-mint) changes. Stale pipelines are cancelled at
 * every await point AND at the worker (superseded computes reject), so an
 * image switched mid-compute never surfaces its result.
 */
export function useImageHistogram(
  image: ImageRef | null,
): UseImageHistogramResult {
  const [state, setState] = useState<UseImageHistogramResult>({
    status: "idle",
    histogram: null,
  });

  // One worker client per hook mount, spawned lazily on first compute.
  const clientRef = useRef<HistogramClient | null>(null);
  useEffect(() => {
    return () => {
      clientRef.current?.dispose();
      clientRef.current = null;
    };
  }, []);

  const imageId = image?.imageId ?? null;
  const fullUrl = image?.urls.full ?? null;

  useEffect(() => {
    if (!imageId || !fullUrl) {
      setState({ status: "idle", histogram: null });
      return;
    }

    const run = { cancelled: false };
    // Accessor (not a direct property read) so TypeScript's control-flow
    // narrowing can't decide the flag "is always false" past the first
    // checkpoint — the cleanup genuinely flips it between awaits.
    const isCancelled = (): boolean => run.cancelled;
    setState({ status: "loading", histogram: null });

    void (async () => {
      try {
        const blob = await resolveImageBlob(fullUrl, isCancelled);
        if (isCancelled() || blob === null) return;
        const imageData = await downsampleToImageData(blob);
        if (isCancelled()) return;
        clientRef.current ??= createHistogramClient();
        const histogram = await clientRef.current.compute(imageData);
        if (isCancelled()) return;
        setState({ status: "ready", histogram });
      } catch (error) {
        // A superseded compute means a newer image's pipeline owns the
        // state now — leave it alone. Anything else is a real failure.
        if (isCancelled() || error instanceof StaleComputeError) return;
        // Expired SAS (403) is NOT terminal — the queue/viewer path
        // re-mints the image list on the same 403 (CACHE-02 hooks), the
        // list refetch rotates `urls.full`, and this effect re-runs with
        // the fresh URL. Stay in "loading" and ride the re-mint rather
        // than flashing an error the rotation is about to fix.
        if (error instanceof ImageFetchError && error.isSasExpired) return;
        setState({ status: "error", histogram: null });
      }
    })();

    return () => {
      run.cancelled = true;
    };
  }, [imageId, fullUrl]);

  return state;
}
